/**
 * `/hr/documents` — the Employee Documents & Contracts screen, WP-W6.
 * `web/shapes/hr-documents.md` is the agreed shape, written before this file.
 *
 * The first HR (people-ops) surface in the SPA. It copies the recruiting candidate
 * pattern — a metadata list, a register-and-presigned-PUT upload, and the one sensitive,
 * access-logged **download** gated on `X-Access-Reason` — and adds the employment
 * **contracts** of the same employee.
 *
 * ── The employee id is typed, not picked ─────────────────────────────────────
 *
 * Every read here is keyed by an employee id. `GET /employees` (Task T2-A) and
 * `EmployeePicker` (Task T2-B) exist now, but this screen has not been rewired to use
 * them, so the id still comes from a text field — the same pattern the recruiting
 * screens use for their recruiter/hiring-manager id inputs. Nothing is asked until an id is
 * entered (`useApiQuery(null)` parks in loading with no request in flight); naming an id
 * asserts no scope — the server resolves the WHOSE gate over the caller's tree and
 * answers `403`/`404` for an employee outside it.
 *
 * ── Sensitive only at the download ──────────────────────────────────────────
 *
 * The document *metadata* list and the contracts list carry no `x-wp-sensitive-access`,
 * so nothing here sends an `X-Access-Reason` for them — collecting a reason the server
 * neither requires nor stores would be under false pretences. Only `GET
 * …/documents/{docId}/download` is sensitive, so the reason is asked **per download**, in
 * the documents panel, not as a gate over the whole screen.
 *
 * Nothing is applied optimistically. A read refused renders `ApiErrorNote` in its region
 * and leaves the others standing; a write refused renders the server's own sentence and
 * leaves the form's input in place.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { useSession } from '../../auth/SessionProvider';
import type {
  HrContract,
  HrContractCreateRequest,
  HrContractListResponse,
  HrDocumentType,
  HrEmployeeDocument,
  HrEmployeeDocumentListResponse,
} from '../../api/hr';
import { hrEmployeeContractsPath, hrEmployeeDocumentsPath } from '../../api/hr';
import { useApiMutation } from '../../api/useApiMutation';
import { useApiQuery } from '../../api/useApiQuery';
import {
  deliverBlobDownload,
  downloadEmployeeDocument,
  registerAndUploadDocument,
  type DeliverBlob,
  type DownloadState,
  type UploadState,
} from '../../hr/employee-document';
import { EmptyState } from '../../states/EmptyState';
import { ScreenHeading } from '../../shell/ScreenHeading';
import { EmployeeDocumentsPanel } from './EmployeeDocumentsPanel';
import { ContractsPanel } from './ContractsPanel';

const DEFAULT_TIMEZONE = 'Asia/Ho_Chi_Minh';

export interface EmployeeDocumentsScreenProps {
  /**
   * How a fetched HR document is handed to the operator. Injected so a test can drive
   * the download without `URL.createObjectURL`, which jsdom does not implement;
   * production uses the real object-URL-and-anchor delivery.
   */
  readonly deliverDocument?: DeliverBlob;
}

export function EmployeeDocumentsScreen({
  deliverDocument = deliverBlobDownload,
}: EmployeeDocumentsScreenProps = {}): ReactNode {
  const { t, locale } = useI18n();
  const { user, client } = useSession();
  const timezone = user?.timezone ?? DEFAULT_TIMEZONE;

  const [employeeIdInput, setEmployeeIdInput] = useState('');
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [docsToken, setDocsToken] = useState(0);
  const [contractsToken, setContractsToken] = useState(0);

  const [registerState, setRegisterState] = useState<UploadState>({ status: 'idle' });
  const [downloadState, setDownloadState] = useState<DownloadState>({ status: 'idle' });

  const docsPath = employeeId === null ? null : hrEmployeeDocumentsPath(employeeId);
  const docs = useApiQuery<HrEmployeeDocumentListResponse>(docsPath, docsToken);

  const contractsPath = employeeId === null ? null : hrEmployeeContractsPath(employeeId);
  const contracts = useApiQuery<HrContractListResponse>(contractsPath, contractsToken);

  const createContract = useApiMutation<HrContractCreateRequest, HrContract>(
    hrEmployeeContractsPath('unset'),
    'POST',
  );

  const loadEmployee = (id: string): void => {
    setRegisterState({ status: 'idle' });
    setDownloadState({ status: 'idle' });
    createContract.reset();
    setEmployeeId(id);
  };

  const registerDocument = (file: File, docType: HrDocumentType): void => {
    if (employeeId === null) return;
    setRegisterState({ status: 'working' });
    registerAndUploadDocument(client, employeeId, file, docType)
      .then((next) => {
        setRegisterState(next);
        if (next.status === 'done') setDocsToken((value) => value + 1);
      })
      .catch(() => {
        /* `registerAndUploadDocument` resolves with a `failed` state rather than rejecting. */
      });
  };

  const downloadDocument = (doc: HrEmployeeDocument, reason: string): void => {
    if (employeeId === null) return;
    setDownloadState({ status: 'requesting', docId: doc.id });
    downloadEmployeeDocument(client, employeeId, doc, reason, deliverDocument)
      .then(setDownloadState)
      .catch(() => {
        /* resolves with a `refused` state rather than rejecting. */
      });
  };

  const submitContract = (body: HrContractCreateRequest): void => {
    if (employeeId === null) return;
    createContract
      .submit(body, hrEmployeeContractsPath(employeeId))
      .then(() => {
        setContractsToken((value) => value + 1);
      })
      .catch(() => {
        /* `submit` resolves rather than rejects. */
      });
  };

  return (
    <div className="wp-stack wp-f">
      <ScreenHeading>{t('nav.hrDocuments')}</ScreenHeading>
      <div className="wp-f-lede">
        <p className="wp-small wp-muted">{t('hr.documents.screenIntro')}</p>
        <p className="wp-small wp-muted" data-testid="w6d-scope-note">
          {t('hr.documents.scopeNote')}
        </p>
      </div>

      {/* ── Which employee ─────────────────────────────────────────────────── */}
      <section
        className="wp-section"
        aria-labelledby="w6d-employee-heading"
        data-testid="w6d-employee"
      >
        <h2 className="wp-section__title" id="w6d-employee-heading">
          {t('hr.employee.title')}
        </h2>
        <p className="wp-small wp-muted">{t('hr.employee.intro')}</p>
        <form
          className="wp-filters"
          onSubmit={(event) => {
            event.preventDefault();
            const trimmed = employeeIdInput.trim();
            if (trimmed === '') return;
            loadEmployee(trimmed);
          }}
        >
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w6d-employee-id">
              {t('hr.employee.idLabel')}
            </label>
            <input
              className="wp-field__input"
              id="w6d-employee-id"
              data-testid="w6d-employee-id"
              type="text"
              inputMode="numeric"
              value={employeeIdInput}
              autoComplete="off"
              onChange={(event) => {
                setEmployeeIdInput(event.target.value);
              }}
            />
            <p className="wp-small wp-muted">{t('hr.employee.idHint')}</p>
          </div>
          <div className="wp-filters__actions">
            <button
              className="wp-button wp-button--primary"
              type="submit"
              data-testid="w6d-employee-load"
              disabled={employeeIdInput.trim() === ''}
            >
              {t('hr.employee.load')}
            </button>
          </div>
        </form>
      </section>

      {employeeId === null ? (
        <EmptyState
          title={t('hr.employee.none.title')}
          description={t('hr.employee.none.body')}
          testId="w6d-employee-none"
        />
      ) : (
        <>
          <EmployeeDocumentsPanel
            status={docs.status}
            documents={docs.data?.data ?? null}
            readError={docs.error}
            onReload={docs.reload}
            locale={locale}
            timezone={timezone}
            registerState={registerState}
            onRegister={registerDocument}
            downloadState={downloadState}
            onDownload={downloadDocument}
          />

          <ContractsPanel
            status={contracts.status}
            contracts={contracts.data?.data ?? null}
            readError={contracts.error}
            onReload={contracts.reload}
            locale={locale}
            timezone={timezone}
            createPending={createContract.status === 'pending'}
            createError={createContract.error}
            created={createContract.status === 'ok' ? createContract.data : null}
            onCreate={submitContract}
          />
        </>
      )}
    </div>
  );
}
